import { LuLoaderCircle } from "react-icons/lu";
import { useIsCompactView } from "../../../core_stores/useIsCompactViewStore";
import { SubscriptionProduct } from "../../../model/subscriptionProduct";

export default function SubscriptionsModalItem({
  product,
  isDisabled,
  isLoading,
  onSelect,
}: {
  product: SubscriptionProduct;
  isDisabled: boolean;
  isLoading: boolean;
  onSelect: () => void;
}) {
  const isCompactView = useIsCompactView();

  return (
    <div
      key={product.name}
      className={`h-full ${isCompactView && "min-h-[300px]"} ${
        product.status !== "active"
          ? "bg-gradient-to-b from-[#C74BEB] to-[#712A85]"
          : "bg-primary-blue dark:bg-[#646971]"
      }
      p-[2px] rounded-md overflow-hidden
      shadow-[0_2px_21.2px_-1px_rgba(6,4,43,0.25),0_2px_5.9px_0_rgba(4,3,26,0.18)]`}
    >
      <div
        className="w-full h-full px-4 py-5 rounded-[4px]
        flex flex-col items-center justify-between
      bg-light-surface-secondary dark:bg-dark-surface-secondary
      text-light-text dark:text-dark-text"
      >
        <Description
          planName={product.name}
          tokens={product.marketing_features?.join(", ")}
          description={product.description}
        />
        <Divider />
        <Price price={product.price} currency={product.currency} />
        <Divider />
        <BottomPart
          buttonId={`subscriptions-modal-item-${product.name
            .toLowerCase()
            .replace(/ /g, "-")}`}
          status={product.status}
          onClick={onSelect}
          isDisabled={isDisabled}
          isLoading={isLoading}
        />
      </div>
    </div>
  );
}

function Description({
  planName,
  tokens,
  description,
}: {
  planName: string;
  tokens: string;
  description: string;
}) {
  return (
    <div
      className="h-[55%] flex flex-col gap-4 
    items-center text-center pb-4"
    >
      <div className="text-2xl font-semibold">{planName}</div>
      <div>{tokens}</div>
      <div
        className={`text-sm text-light-placeholder dark:text-dark-placeholder`}
      >
        {description}
      </div>
    </div>
  );
}

function Price({ price, currency }: { price: number; currency: string }) {
  const formatCurrency = (amount: number, currencyCode: string) => {
    try {
      // Convert amount from cents to dollars (divide by 100)
      const amountInCurrency = amount / 100;
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: currencyCode.toUpperCase(),
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(amountInCurrency);
    } catch (error) {
      return `${currencyCode.toUpperCase()}${amount / 100}`;
    }
  };

  return (
    <div className="h-[20%] flex flex-col items-center justify-center py-2">
      <div className={`text-4xl`}>
        {formatCurrency(price, currency)}
        {price > 0 && <span className="text-lg">/month</span>}
      </div>
    </div>
  );
}

function Divider() {
  return (
    <hr
      className={`h-px w-full duration-500
        border-[#8B8B8B4D] dark:border-[#8B8B8B4D]`}
    />
  );
}

function BottomPart({
  buttonId,
  status,
  isDisabled,
  isLoading,
  onClick,
}: {
  buttonId: string;
  status: string;
  isDisabled: boolean;
  isLoading: boolean;
  onClick: () => void;
}) {
  return (
    <div className="h-[25%] w-full flex items-end justify-center pt-4">
      {status === "active" ? (
        <div
          className="h-[48px] font-semibold
        flex items-center justify-center"
        >
          Active
        </div>
      ) : (
        <button
          id={buttonId}
          className={`${
            status === "active"
              ? "bg-primary-blue"
              : "bg-gradient-to-r from-fuchsia-500 to-violet-500"
          } text-white w-full h-[48px] max-w-[240px]
            rounded-md font-semibold
            hover:brightness-125 transition-all duration-300
      hover:cursor-pointer`}
          onClick={onClick}
          disabled={isDisabled}
        >
          {isLoading ? (
            <div className="flex items-center justify-center">
              <LuLoaderCircle className="animate-spin" size={20} />
            </div>
          ) : (
            status[0].toUpperCase() + status.slice(1)
          )}
        </button>
      )}
    </div>
  );
}
